if (!customElements.get('wlsn-cart-subscription')) {
  class WLSNCartSubscription extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        loading: 'loading',
        optionsSelector: 'wlsn-options-selector',
        subscribeButton: 'js-cart-item-subscription-change-button',
      };

      this.isLoading = false;
      this.lineKey = this.dataset.lineKey;
      this.currentSellingPlanId = this.dataset.sellingPlanId || null;

      this.bindMethods = {
        handleSellingPlanChange: this._handleSellingPlanChange.bind(this),
        handleSubscribeClick: this._handleSubscribeClick.bind(this),
        handleUpdateStart: this._handleUpdateStart.bind(this),
        handleUpdateSuccess: this._handleUpdateSuccess.bind(this),
        handleUpdateError: this._handleUpdateError.bind(this),
      };
    }

    get elements() {
      return {
        optionsSelector: this.querySelector(`${this.classes.optionsSelector}`),
        subscribeButton: this.querySelector(`.${this.classes.subscribeButton}`),
      };
    }

    connectedCallback() {
      // Initialize from data attributes
      this.lineKey = this.dataset.lineKey;
      this.lineIndex = parseInt(this.dataset.lineIndex);
      this.currentSellingPlanId = this.dataset.sellingPlanId || null;
      this.currentQuantity = parseInt(this.dataset.quantity) || 1;

      if (!this.lineKey) {
        console.error('CRITICAL: lineKey is missing!', this.dataset);
      }

      if (!this.lineIndex) {
        console.error('CRITICAL: lineIndex is missing!', this.dataset);
      }

      this._attachEventListeners();
    }

    disconnectedCallback() {
      this._detachEventListeners();
    }

    _attachEventListeners() {
      const { optionsSelector, subscribeButton } = this.elements;

      // Listen for option selector changes (when subscription plan is selected)
      if (optionsSelector) {
        const hiddenInput = optionsSelector.querySelector('input[type="hidden"]');
        if (hiddenInput) {
          hiddenInput.addEventListener('change', this.bindMethods.handleSellingPlanChange);
        }
      }

      // Listen for subscribe button click (when no subscription is active)
      if (subscribeButton) {
        subscribeButton.addEventListener('click', this.bindMethods.handleSubscribeClick);
      }

      // Listen for cart update events from main cart
      document.addEventListener('wlsn:cart:update:start', this.bindMethods.handleUpdateStart);
      document.addEventListener('wlsn:cart:update:success', this.bindMethods.handleUpdateSuccess);
      document.addEventListener('wlsn:cart:update:error', this.bindMethods.handleUpdateError);
    }

    _detachEventListeners() {
      const { optionsSelector, subscribeButton } = this.elements;

      if (optionsSelector) {
        const hiddenInput = optionsSelector.querySelector('input[type="hidden"]');
        if (hiddenInput) {
          hiddenInput.removeEventListener('change', this.bindMethods.handleSellingPlanChange);
        }
      }

      if (subscribeButton) {
        subscribeButton.removeEventListener('click', this.bindMethods.handleSubscribeClick);
      }

      document.removeEventListener('wlsn:cart:update:start', this.bindMethods.handleUpdateStart);
      document.removeEventListener('wlsn:cart:update:success', this.bindMethods.handleUpdateSuccess);
      document.removeEventListener('wlsn:cart:update:error', this.bindMethods.handleUpdateError);
    }

    _handleSellingPlanChange(event) {
      if (this.isLoading) return;

      // Get value from the hidden input that changed
      const newSellingPlanId = event.target.value;

      // Normalize comparison (convert both to strings)
      const normalizedNew = String(newSellingPlanId);
      const normalizedCurrent = String(this.currentSellingPlanId || '');

      if (!normalizedNew || normalizedNew === normalizedCurrent) {
        return;
      }

      window.WLSNHelpers.triggerEvent(this, 'wlsn:cart:subscription:change', {
        lineKey: this.lineKey,
        lineIndex: this.lineIndex,
        sellingPlanId: normalizedNew,
        previousSellingPlanId: normalizedCurrent || null,
        quantity: this.currentQuantity,
      });
    }

    _handleSubscribeClick(event) {
      event.preventDefault();

      if (this.isLoading) return;

      // Get the first available selling plan ID from data attribute
      const firstSellingPlanId = this.dataset.firstSellingPlanId;

      if (!firstSellingPlanId) {
        return;
      }

      window.WLSNHelpers.triggerEvent(this, 'wlsn:cart:subscription:change', {
        lineKey: this.lineKey,
        lineIndex: this.lineIndex,
        sellingPlanId: firstSellingPlanId,
        previousSellingPlanId: null,
        quantity: this.currentQuantity,
      });
    }

    _handleUpdateStart(event) {
      const { lineKey, action } = event.detail;
      if (lineKey === this.lineKey && action === 'subscription') {
        this._setLoadingState(true);
      }
    }

    _handleUpdateSuccess(event) {
      // Cart will be re-rendered, just remove loading state
      this._setLoadingState(false);
    }

    _handleUpdateError(event) {
      const { lineKey } = event.detail;
      if (lineKey === this.lineKey) {
        this._setLoadingState(false);
      }
    }

    _setLoadingState(loading) {
      this.isLoading = loading;
      const { optionsSelector } = this.elements;

      this.classList.toggle(this.classes.loading, loading);
      if (optionsSelector) {
        optionsSelector.setDisabledState(loading);
        optionsSelector.setLoadingState(loading);
      }
    }
  }

  customElements.define('wlsn-cart-subscription', WLSNCartSubscription);
}
